/**
 * FilterBox — per-column wrapper that lives in the MRT filter row
 * and dispatches to the right filter component (text / select / date
 * / currency / price / select-with-dynamic-hint).
 *
 * Logic is unchanged vs. the legacy version: the same dispatcher,
 * the same filter contract ({ dataKey, filterValue, onChange,
 * tableFilters, tableName, … }), the same `column.setFilterValue`
 * wiring. Only the surrounding chrome is new — a calm, borderless
 * container that lets each filter's own input supply the shape, so
 * text / select / date don't all sit inside a second rounded pill.
 */
import { useRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import cx from 'classnames';
import CurrencyTextFilter from './CurrencyTextFilter';
import SelectWithDynamicHintFilter from './SelectWithDynamicHintFilter/SelectWithDynamicHintFilter';
import PriceFilter from './PriceFilter';
import FILTER_TYPES from './constants';
import DateFilter from './DateFilter';
import SelectFilter from './SelectFilter';
import TextFilter from './TextFilter';

const PREFIX = 'FilterBox';
const classes = {
  filterBox: `${PREFIX}-filterBox`,
  isEditableModeStyle: `${PREFIX}-isEditableModeStyle`,
  transparent: `${PREFIX}-transparent`,
  specialDateFilterPadding: `${PREFIX}-specialDateFilterPadding`,
};

const Root = styled('div')({
  display: 'contents',
  [`&.${classes.filterBox}`]: {
    position: 'relative',
    width: '100%',
    minWidth: '100%',
    height: 32,
    minHeight: 32,
    padding: '0 4px',
    display: 'flex',
    alignItems: 'center',
    background: 'transparent',
  },
  [`&.${classes.isEditableModeStyle}`]: {
    width: '100%',
  },
  // Disabled (non-filterable) columns keep the same footprint so the
  // row doesn't jitter, they just dim their content.
  [`&.${classes.transparent}`]: {
    opacity: 0.5,
  },
  // Date filters render a trigger pill, not a text input — they need
  // a different cursor and slightly more breathing room.
  [`&.${classes.specialDateFilterPadding}`]: {
    padding: '0 8px',
    cursor: 'default',
  },
});

function FilterBox(props) {
  const containerElement = useRef();
  const {
    columnData: {
      filterType,
      isFilterable,
      includeCurrentAccount,
      customAccountUuidForFilter,
      // Static pre-filter entries the column's typeahead lookup
      // must always send alongside the user's search — e.g. the
      // L2 CPT Mix column needs `q=targetAccountUuid:<uuid>` so
      // the lookup returns only the drilled-into account's CPTs.
      // Shape: [{ id, value }] using the same contract as the
      // table's own filter state.
      staticFilters,
      // Columns whose API refuses a negated term (422). Hides the
      // include / exclude switch in the dynamic-hint filter.
      disableExcludeMode,
    },
    isEditableMode,
  } = props;
  const renderFilter = () => {
    const state = props.table?.getState();
    const filterProps = {
      ...props,
      filterValue: state.filter.find(element => element.id === props?.column.id)?.value,
      onChange: (k, v) => {
        props?.column?.setFilterValue(v, true);
      },
      tableFilters: state.filter,
      tableName: state.tableName,
      dataKey: props?.column.id,
      includeCurrentAccount: includeCurrentAccount,
      customAccountUuidForFilter: customAccountUuidForFilter,
      staticFilters: staticFilters,
      disableExcludeMode: disableExcludeMode,
    };

    switch (filterType) {
      case FILTER_TYPES.DATE: {
        return <DateFilter {...filterProps} containerElement={containerElement} />;
      }
      case FILTER_TYPES.SELECT: {
        return <SelectFilter {...filterProps} containerElement={containerElement} />;
      }
      case FILTER_TYPES.CHECKBOX: {
        return <SelectFilter {...filterProps} containerElement={containerElement} />;
      }
      case FILTER_TYPES.INPUT_CURRENCY: {
        return <CurrencyTextFilter {...filterProps} />;
      }
      case FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT: {
        return <SelectWithDynamicHintFilter {...filterProps} />;
      }
      case FILTER_TYPES.PRICE: {
        return <PriceFilter {...filterProps} />;
      }
      default:
        return <TextFilter {...filterProps} />;
    }
  };

  return (
    <Root
      ref={containerElement}
      className={cx(classes.filterBox, {
        [classes.transparent]: !isFilterable,
        [classes.specialDateFilterPadding]: filterType === FILTER_TYPES.DATE,
        [classes.isEditableModeStyle]: isEditableMode,
      })}
    >
      {renderFilter()}
    </Root>
  );
}

FilterBox.propTypes = {
  columnData: PropTypes.shape({
    filterType: PropTypes.oneOf([FILTER_TYPES.DATE, FILTER_TYPES.INPUT, FILTER_TYPES.SELECT]),
    isFilterable: PropTypes.bool,
  }).isRequired,
};

export default FilterBox;
